import { create } from 'zustand';
import { shoppingListDomain } from '@/services/domains';
import type {
  AddToShoppingListRequest,
  ShoppingList,
  UpdateShoppingListItemRequest,
} from '@/types/shoppingList';

interface ShoppingListState {
  shoppingList: ShoppingList | null;
  loading: boolean;
  mutating: boolean;
  error: string | null;
  initialized: boolean;
  fetchList: () => Promise<void>;
  addItems: (request: AddToShoppingListRequest) => Promise<void>;
  updateItem: (itemId: string, update: UpdateShoppingListItemRequest) => Promise<void>;
  toggleItemCheck: (itemId: string, isChecked: boolean) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  clearList: () => Promise<void>;
  emailList: (email: string) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState: Pick<ShoppingListState, 'shoppingList' | 'loading' | 'mutating' | 'error' | 'initialized'> = {
  shoppingList: null,
  loading: false,
  mutating: false,
  error: null,
  initialized: false,
};

const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  return 'Unexpected error occurred. Please try again.';
};

export const useShoppingListStore = create<ShoppingListState>((set, get) => ({
  ...initialState,

  setError: (error: string | null) => set({ error }),

  fetchList: async () => {
    set({ loading: true, error: null });
    try {
      const list = await shoppingListDomain.getShoppingList();
      set({ shoppingList: list, initialized: true });
    } catch (err) {
      set({ error: errorMessage(err), shoppingList: null, initialized: true });
    } finally {
      set({ loading: false });
    }
  },

  addItems: async (request: AddToShoppingListRequest) => {
    set({ mutating: true, error: null });
    try {
      const list = await shoppingListDomain.addItems(request);
      set({ shoppingList: list });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    } finally {
      set({ mutating: false });
    }
  },

  updateItem: async (itemId: string, update: UpdateShoppingListItemRequest) => {
    set({ mutating: true, error: null });
    try {
      const updatedItem = await shoppingListDomain.updateItem(itemId, update);
      const current = get().shoppingList;
      if (!current) {
        const list = await shoppingListDomain.getShoppingList();
        set({ shoppingList: list });
        return;
      }
      set({
        shoppingList: {
          ...current,
          items: current.items.map(item => (item.id === itemId ? updatedItem : item)),
        },
      });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    } finally {
      set({ mutating: false });
    }
  },

  toggleItemCheck: async (itemId: string, isChecked: boolean) => {
    await get().updateItem(itemId, { isChecked });
  },

  updateQuantity: async (itemId: string, quantity: number) => {
    await get().updateItem(itemId, { quantity });
  },

  deleteItem: async (itemId: string) => {
    set({ mutating: true, error: null });
    try {
      await shoppingListDomain.deleteItem(itemId);
      const current = get().shoppingList;
      if (!current) {
        const list = await shoppingListDomain.getShoppingList();
        set({ shoppingList: list });
        return;
      }
      set({
        shoppingList: {
          ...current,
          items: current.items.filter(item => item.id !== itemId),
        },
      });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    } finally {
      set({ mutating: false });
    }
  },

  clearList: async () => {
    set({ mutating: true, error: null });
    try {
      await shoppingListDomain.clearList();
      const current = get().shoppingList;
      set({
        shoppingList: { id: current?.id ?? '', items: [], updatedAt: new Date().toISOString() },
      });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    } finally {
      set({ mutating: false });
    }
  },

  emailList: async (email: string) => {
    set({ mutating: true, error: null });
    try {
      await shoppingListDomain.emailList({ email });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    } finally {
      set({ mutating: false });
    }
  },

  reset: () => set({ ...initialState }),
}));
